// src/App.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import Header from './components/layout/Header';
import './App.css';
import { Box, CircularProgress, Typography, Paper, Button, Link } from '@mui/material';
import TickerTape from './components/ui/TickerTape';
import { AuthProvider, useAuth } from './context/AuthContext';
import OnsaImpact from './assets/images/Frame 4.png'; // logo
import HowToGuideButton from './components/ui/HowToGuideButton';

// Allow multiple parent origins via a comma-separated env list
const useAllowedParents = () => {
  return useMemo(() => {
    const envList = (process.env.REACT_APP_PARENT_ORIGINS || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const defaults = [
      'https://client.onsafx.com',
      'https://portal.onsafx.com',
      // 'https://onsafx.com',
      // 'http://localhost:3000',
    ];
    const current = typeof window !== 'undefined' ? window.location.origin : undefined;
    return [...envList, current, ...defaults].filter(Boolean);
  }, []);
};

function AppInner() {
  const { loginByEmail, hasAccessToken } = useAuth();
  const ALLOWED_PARENTS = useAllowedParents();

  // Guards
  const inFlightRef = useRef(false);       // is a login call currently running?
  const lastEmailRef = useRef(null);       // last processed email
  const ackedRef = useRef(false);          // acknowledgement already sent?
  const parentOriginRef = useRef('*');     // parent origin (if known)
  const lastSentHeightRef = useRef(0);     // last height posted to parent

  // Loading gate
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const listener = async (event) => {
      if (!ALLOWED_PARENTS.includes(event.origin)) return;

      // Store parent origin so responses go to the correct origin
      parentOriginRef.current = event.origin;

      const data = event?.data;
      if (data?.type === 'USER_EMAIL') {
        const newEmail = data?.email?.trim();
        if (!newEmail) return;

        // Prevent duplicate processing for the same email
        if (lastEmailRef.current === newEmail) return;

        // Queue while a login call is already running
        if (inFlightRef.current) return;

        inFlightRef.current = true;
        try {
          // Persist extra user context sent by parent (tier, admin_email)
          if (data?.tier != null) localStorage.setItem('user_tier', data?.tier);
          if (data?.admin_email) localStorage.setItem('admin_email', data?.admin_email);
          await loginByEmail(newEmail);
          lastEmailRef.current = newEmail;

          // Login succeeded -> unlock the app
          setReady(true);

          // Send ACK once on success
          if (!ackedRef.current && window.parent && window.parent !== window) {
            const target = parentOriginRef.current;
            if (target && target !== '*') {
              ackedRef.current = true;
              window.parent.postMessage({ type: 'CR_ACK' }, target);
            }
          }
        } catch (e) {
          console.error('loginByEmail failed:', e);
          setReady(true); // keep the app accessible even when login fails
        } finally {
          inFlightRef.current = false;
        }
      }
    };

    window.addEventListener('message', listener);

    // Fallback: open the app after 2s even if no message arrives
    const fallbackTimer = setTimeout(() => {
      if (!ready) setReady(true);
    }, 2000);

    // Automatic iframe height reporting
    const sendHeight = () => {
      if (!window.parent || window.parent === window) return;
      const h = Math.max(
        document.documentElement?.scrollHeight || 0,
        document.body?.scrollHeight || 0
      );
      if (!h) return;
      // Avoid sending the same value repeatedly
      if (Math.abs(h - lastSentHeightRef.current) < 2) return;
      lastSentHeightRef.current = h;
      try {
        const target = parentOriginRef.current;
        if (target && target !== '*') {
          window.parent.postMessage({ type: 'CR_HEIGHT', height: h }, target);
        }
      } catch {
        // swallow; will try again on next resize/interval
      }
    };

    // React to content changes using ResizeObserver
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(sendHeight);
    });
    try { ro.observe(document.body); } catch { /* ignore */ }

    // Measure ticker height and publish as a CSS variable
    const tape = document.getElementById('tickerTape');
    const updateTapeVar = () => {
      const th = tape?.getBoundingClientRect?.().height || 48;
      try { document.documentElement.style.setProperty('--tape-h', `${Math.round(th)}px`); } catch { /* ignore */ }
    };
    updateTapeVar();
    let roTape;
    if (typeof ResizeObserver !== 'undefined' && tape) {
      roTape = new ResizeObserver(updateTapeVar);
      try { roTape.observe(tape); } catch { /* ignore */ }
    }

    // Initial measurement + resize/load triggers
    const onLoad = () => { updateTapeVar(); sendHeight(); };
    const onResize = () => { updateTapeVar(); sendHeight(); };
    window.addEventListener('load', onLoad);
    window.addEventListener('resize', onResize);
    const interval = window.setInterval(() => { updateTapeVar(); sendHeight(); }, 1000);

    return () => {
      window.removeEventListener('message', listener);
      window.removeEventListener('load', onLoad);
      window.removeEventListener('resize', onResize);
      window.clearInterval(interval);
      clearTimeout(fallbackTimer);
      try { ro.disconnect(); } catch { /* ignore */ }
      try { roTape && roTape.disconnect(); } catch { /* ignore */ }
    };
  }, [loginByEmail, ALLOWED_PARENTS, ready]);

  // Global loading screen
  if (!ready) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
          backgroundColor: 'white',
          color: 'white',
          gap: 3,
        }}
      >
        <Box
          component="img"
          src={OnsaImpact}
          alt="Onsa Impact Logo"
          sx={{ width: 180, mb: 2 }}
        />
        <CircularProgress sx={{ color: '#04477f' }} />
        <Typography variant="h6" sx={{ color: '#04477f' }}>Loading...</Typography>
      </Box>
    );
  }

  if (!hasAccessToken) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          backgroundColor: '#ffffff',
        }}
      >
        <Paper
          elevation={10}
          sx={{
            maxWidth: 560,
            width: '100%',
            p: { xs: 4, md: 5 },
            borderRadius: 4,
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(3,27,57,0.92) 0%, rgba(10,68,122,0.88) 45%, rgba(18,137,201,0.9) 100%)',
            color: '#ffffff',
            boxShadow: '0 24px 48px rgba(5, 34, 64, 0.35)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <Box
            component="img"
            src="https://onsafx.com/wp-content/uploads/2025/03/Onsa-logo-1.svg"
            alt="OnsaFX Logo"
            sx={{ width: { xs: 160, md: 190 }, mx: 'auto', mb: 3 }}
          />

          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Not an OnsaFX member yet?
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
            Connect your account to unlock live market dashboards, tailored news, and professional research tools.
            Visit our website to join the community and start exploring OnsaFX today.
          </Typography>

          <Button
            variant="contained"
            href="https://onsafx.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: 999,
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #0d5fa5 0%, #0b8dd9 100%)',
              boxShadow: '0 16px 32px rgba(13, 95, 165, 0.3)',
              mb: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #0b8dd9 0%, #0d5fa5 100%)',
              },
            }}
          >
            Visit onsafx.com
          </Button>

          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)' }}>
            FSCA License No. 53192 · FinCEN Registration No. 31000301383062
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.7)' }}>
            For more information, visit{' '}
            <Link href="https://onsafx.com" target="_blank" rel="noopener noreferrer" underline="always" color="inherit" sx={{ fontWeight: 600 }}>
              onsafx.com
            </Link>
            .
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Router>
      <Box display="flex" flexDirection="column" minHeight="100dvh">
        <Header />
        <Box flex="1" sx={{ paddingBottom: { xs: 'var(--tape-h, 48px)', md: 'var(--tape-h, 48px)' } }}>
          <AppRoutes />
        </Box>
        <Box
          id="tickerTape"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: '100%',
            zIndex: 1300,
            backgroundColor: 'white',
            borderTop: '1px solid #ccc',
            willChange: 'height',
          }}
        >
          <TickerTape />
        </Box>
        <HowToGuideButton />
      </Box>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppInner />
    </AuthProvider>
  );
}
